import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import api from "../../../conf/api";
import Step1GroupAndFeedSelection from "./steps/Step1GroupAndFeedSelection";
import Step3VideoSelection from "./steps/Step3VideoSelection";
import StepVideoSource, { isVideoSourcePairResolved, DEFAULT_PAIR_CONFIG } from "./steps/StepVideoSource";
import Step4ReviewSubmit from "./steps/Step4ReviewSubmit";
import ApprovalsPanel from "./ApprovalsPanel";
import { VideoSource, ThumbnailSource } from "../Enums/enums";
import { runWithConcurrency } from "../utils/chunkUploader";
import { uploadFileToS3 } from "../utils/s3Uploader";

const STEPS = [
  "Categories/Partners & Feeds",
  "Select Video(s)",
  "Configure Video Source",
  "Review & Submit",
];

// How many (feed, video) pairs upload/submit concurrently in handleSubmit.
// Each pair's own file upload already opens several concurrent chunk
// requests, so this stays modest rather than firing every pair at once.
const PAIR_SUBMIT_CONCURRENCY = 3;

function isStepValid(step, { selectedGroups, selectedFeedIds, selectedVideos, groupFeeds, videoSourceConfig }) {
  if (step === 0) return selectedGroups.length > 0 && selectedFeedIds.length > 0;
  if (step === 1) return selectedVideos.length > 0;
  if (step === 2) {
    // Every pair must have whatever its source claims actually attached —
    // same rule as the "Ready"/"Not configured" chip in StepVideoSource.js,
    // reused here so a pair like thumbnail_source=CUSTOM with no file picked
    // (or removed) can't reach Review & Submit. Since the default is now
    // Local Upload (DEFAULT_PAIR_CONFIG), an untouched pair counts as
    // unresolved until its file is picked.
    const feeds = (groupFeeds || []).filter((f) => selectedFeedIds.includes(f.id));
    return selectedVideos.every((video) =>
      feeds.every((feed) =>
        isVideoSourcePairResolved(videoSourceConfig?.[video.unique_key]?.[feed.id])
      )
    );
  }
  return true; // Review is always viewable
}

function NewPushWizard({ onPushed }) {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null); // overall "X/N pairs done" text while handleSubmit uploads/posts
  const [pairProgress, setPairProgress] = useState({}); // { [pairIndex]: { label, percent: 0-100, phase: 'pending'|'uploading-video'|'uploading-thumbnail'|'submitting'|'done'|'error' } } — one entry per (video, feed) pair, updated independently so each video shows its own accurate percentage while pairs upload in parallel

  const [selectedGroups, setSelectedGroups] = useState([]); // [{group_type, group_id, label}]
  const [groupFeeds, setGroupFeeds] = useState([]); // flat, each tagged with _group_type/_group_id/_group_label
  const [selectedFeedIds, setSelectedFeedIds] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [overrides, setOverrides] = useState({}); // { unique_key: { feed_id: { title, description, tags } } } — per (video, feed) pair, since a feed's own "Use Title 2" config can differ from another feed's for the same video
  const [videoSourceConfig, setVideoSourceConfig] = useState({}); // { unique_key: { feed_id: { video_source, local_video_file, render_payload, convert_to_vertical, thumbnail_source, local_thumb_file } } }
  const [forcePush, setForcePush] = useState(false);

  const wizardState = { selectedGroups, selectedFeedIds, selectedVideos, groupFeeds, videoSourceConfig };
  const canGoNext = isStepValid(activeStep, wizardState);
  const isLastStep = activeStep === STEPS.length - 1;

  const handleNext = () => {
    if (canGoNext && !isLastStep) setActiveStep((s) => s + 1);
  };
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleGroupToggle = (group) => {
    const exists = selectedGroups.find(
      (g) => g.group_type === group.group_type && g.group_id === group.group_id
    );
    if (exists) {
      // Removing a group: purge only ITS feeds/selections — Group+Feed are
      // one interactive step now, so toggling one group must not disturb
      // feeds already chosen for other still-selected groups.
      setSelectedGroups((prev) =>
        prev.filter((g) => !(g.group_type === group.group_type && g.group_id === group.group_id))
      );
      setGroupFeeds((prev) =>
        prev.filter((f) => !(f._group_type === group.group_type && f._group_id === group.group_id))
      );
      const removedIds = new Set(
        groupFeeds
          .filter((f) => f._group_type === group.group_type && f._group_id === group.group_id)
          .map((f) => f.id)
      );
      setSelectedFeedIds((prev) => prev.filter((id) => !removedIds.has(id)));
      // Its video x feed pairs go too: their titles and video-source settings
      // belong to feeds that are no longer being sent to.
      const dropFeeds = (prev) => {
        const next = {};
        Object.entries(prev).forEach(([uniqueKey, perFeed]) => {
          const kept = Object.fromEntries(
            Object.entries(perFeed || {}).filter(([feedId]) => !removedIds.has(Number(feedId)) && !removedIds.has(feedId))
          );
          if (Object.keys(kept).length) next[uniqueKey] = kept;
        });
        return next;
      };
      setOverrides(dropFeeds);
      setVideoSourceConfig(dropFeeds);
    } else {
      // Adding a group only appends it — its GroupFeeds sub-component fetches
      // and merges its own feeds in without touching anything else selected.
      setSelectedGroups((prev) => [...prev, group]);
    }
  };

  // Title/description/tags overrides are configured per (video, feed) pair —
  // mirrors handleVideoSourceChange below. Needed because "Use Title 2" is a
  // per-FEED setting (MrssInfo.is_title_2 in Admin Portal): the same video
  // can need its title_2 for one feed and its normal title for another.
  const handleOverrideChange = (uniqueKey, feedId, partial) => {
    setOverrides((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...(prev[uniqueKey] || {}),
        [feedId]: { ...(prev[uniqueKey]?.[feedId] || {}), ...partial },
      },
    }));
  };

  // Video source is configured per (video, feed) pair — the same video can
  // be pushed ORIGINAL to one feed and EDITED (its own watermark/template)
  // to another, mirroring PlatformSourceCard.js's per-platform independence.
  const handleVideoSourceChange = (uniqueKey, feedId, partial) => {
    setVideoSourceConfig((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...(prev[uniqueKey] || {}),
        [feedId]: {
          ...(prev[uniqueKey]?.[feedId] || DEFAULT_PAIR_CONFIG),
          ...partial,
        },
      },
    }));
  };

  /**
   * Take one Category/Partner out from a later step (user request
   * 2026-10-13): it, its feeds and every video x feed pair for them are
   * removed; the videos stay for the others. With none left, back to Step 1.
   */
  const handleRemoveGroup = (group) => {
    handleGroupToggle(group);
    const left = selectedGroups.filter((g) => !(g.group_type === group.group_type && g.group_id === group.group_id));
    if (left.length === 0) setActiveStep(0);
    toast.info(
      left.length === 0
        ? `${group.label} removed — nothing left to send to, so you are back at Step 1.`
        : `${group.label} removed — its feeds and their videos will not be sent.`
    );
  };

  /**
   * Take one video out of the push (from Step 3, like Step 2's ×), with its
   * per-feed titles and video-source settings. With none left, back to
   * Step 2 to pick again.
   */
  const handleRemoveVideo = (uniqueKey) => {
    const left = selectedVideos.filter((v) => v.unique_key !== uniqueKey);
    setSelectedVideos(left);
    const drop = (prev) => {
      if (!prev[uniqueKey]) return prev;
      const { [uniqueKey]: _gone, ...rest } = prev;
      return rest;
    };
    setOverrides(drop);
    setVideoSourceConfig(drop);
    if (left.length === 0) setActiveStep(1);
    toast.info(
      left.length === 0
        ? `${uniqueKey} removed — no videos left, so you are back at Step 2.`
        : `${uniqueKey} removed from this push.`
    );
  };

  const resetWizard = () => {
    setActiveStep(0);
    setSelectedGroups([]);
    setGroupFeeds([]);
    setSelectedFeedIds([]);
    setSelectedVideos([]);
    setOverrides({});
    setVideoSourceConfig({});
    setForcePush(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const feeds = groupFeeds.filter((f) => selectedFeedIds.includes(f.id));
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      const pairs = [];
      for (const feed of feeds) {
        for (const video of selectedVideos) {
          pairs.push({ feed, video });
        }
      }

      // Each (feed, video) pair's upload(s) + push are independent of every
      // other pair, so they run concurrently (capped) instead of being
      // fully serialized behind a single await in a nested for-loop.
      // Progress is tracked PER PAIR (not averaged into one blended number)
      // so each video shows its own accurate percentage while several
      // pairs upload in parallel.
      const pairLabels = pairs.map(
        ({ feed, video }) => `${video.title || video.video_title || video.unique_key} → ${feed.title || feed.name}`
      );
      const initialProgress = {};
      pairLabels.forEach((label, i) => {
        initialProgress[i] = { label, percent: 0, phase: "pending" };
      });
      setPairProgress(initialProgress);

      const updatePairProgress = (pairIndex, partial) =>
        setPairProgress((prev) => ({
          ...prev,
          [pairIndex]: { ...prev[pairIndex], ...partial },
        }));

      let completedPairs = 0;
      const refreshSubmitStatus = () => setSubmitStatus(`${completedPairs}/${pairs.length} pairs done`);
      refreshSubmitStatus();

      // Pushing one video to several feeds means several pairs share the
      // SAME File object. Upload it once and reuse the staged key for every
      // pair that picked it, instead of sending a multi-GB file once per
      // feed. Keyed by File identity; the promise is cached (not just the
      // result) so pairs running concurrently await the same upload rather
      // than each starting their own.
      const stagedUploads = new Map();
      const uploadOnce = (file, kind, onProgress) => {
        if (!stagedUploads.has(file)) {
          stagedUploads.set(
            file,
            uploadFileToS3(file, kind, {
              onProgress: ({ progress }) => onProgress(progress),
            }).then((res) => res.staged_key)
          );
        } else {
          // A pair joining an upload already in flight can't get real
          // byte-level progress for it, so show it as in-progress rather
          // than stuck at 0.
          onProgress(50);
        }
        return stagedUploads.get(file);
      };

      await runWithConcurrency(pairs, async ({ feed, video }, pairIndex) => {
        const ov = overrides[video.unique_key]?.[feed.id] || {};
        const vsc = videoSourceConfig[video.unique_key]?.[feed.id] || DEFAULT_PAIR_CONFIG;
        const pairLabel = pairLabels[pairIndex];

        // Files picked in "Configure Video Source" aren't uploaded yet — they
        // go straight to S3's private staging area now, right before this
        // pair's push (see StepVideoSource.js). Bytes bypass CreatorVault's
        // own server entirely (see utils/s3Uploader.js), and nothing is
        // publicly readable until an admin approves. A failed upload skips
        // this pair's push rather than sending one with a missing file.
        let stagedVideoKey = null;
        let stagedThumbKey = null;
        let uploadFailed = false;

        if (vsc.video_source === VideoSource.LOCAL && vsc.local_video_file) {
          updatePairProgress(pairIndex, { phase: "uploading-video", percent: 0 });
          try {
            stagedVideoKey = await uploadOnce(vsc.local_video_file, "video", (progress) =>
              updatePairProgress(pairIndex, { percent: progress })
            );
          } catch (err) {
            failCount += 1;
            errors.push(`Video upload failed for ${pairLabel}: ${err.response?.data?.error || err.message}`);
            uploadFailed = true;
            updatePairProgress(pairIndex, { phase: "error" });
          }
        }

        if (!uploadFailed && vsc.thumbnail_source === ThumbnailSource.CUSTOM && vsc.local_thumb_file) {
          updatePairProgress(pairIndex, { phase: "uploading-thumbnail", percent: 0 });
          try {
            stagedThumbKey = await uploadOnce(vsc.local_thumb_file, "thumbnail", (progress) =>
              updatePairProgress(pairIndex, { percent: progress })
            );
          } catch (err) {
            failCount += 1;
            errors.push(`Thumbnail upload failed for ${pairLabel}: ${err.response?.data?.error || err.message}`);
            uploadFailed = true;
            updatePairProgress(pairIndex, { phase: "error" });
          }
        }

        if (uploadFailed) {
          completedPairs += 1;
          refreshSubmitStatus();
          return;
        }

        updatePairProgress(pairIndex, { phase: "submitting", percent: 100 });
        const payload = {
          unique_key: video.unique_key,
          title: video.title || video.video_title || "",
          // VideoListFinderView's raw_video join can have a null s3_url even
          // for an otherwise valid result — fall back to portal_url (the
          // watermarked/portal-ready render, always present since the search
          // itself requires one to exist) and social_url before giving up.
          original_video_url:
            video.s3_url || video.original_video_url || video.portal_url || video.social_url || "",
          original_thumbnail_url:
            video.original_thumbnail_url || video.portal_thumb || video.thumbnail_url || video.thumbnail || "",
          admin_portal_group_type: feed._group_type,
          admin_portal_group_label: feed._group_label,
          admin_portal_feed_id: feed.id,
          admin_portal_feed_title: feed.title || feed.name || "",
          is_force_push: forcePush,
          ...(video.is_native_video ? { native_video_id: video.native_video_id } : {}),
          video_source: vsc.video_source || VideoSource.ORIGINAL,
          convert_to_vertical: !!vsc.convert_to_vertical,
          ...(vsc.video_source === VideoSource.LOCAL && stagedVideoKey ? { staged_video_key: stagedVideoKey } : {}),
          ...(vsc.video_source === VideoSource.EDITED && vsc.render_payload
            ? { render_payload: vsc.render_payload }
            : {}),
          // Thumbnail is independent of video_source — see StepVideoSource.js.
          thumbnail_source: vsc.thumbnail_source || ThumbnailSource.ORIGINAL,
          ...(vsc.thumbnail_source === ThumbnailSource.CUSTOM && stagedThumbKey ? { staged_thumb_key: stagedThumbKey } : {}),
          ...(ov.title ? { override_title: ov.title } : {}),
          ...(ov.description ? { override_description: ov.description } : {}),
          ...(ov.tags
            ? { override_tags: ov.tags.split(",").map((t) => t.trim()).filter(Boolean) }
            : {}),
        };
        try {
          await api.post("/mrss/admin-portal/push/", payload);
          successCount += 1;
          updatePairProgress(pairIndex, { phase: "done", percent: 100 });
        } catch (err) {
          failCount += 1;
          const msg =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            `Failed for ${pairLabel}`;
          errors.push(msg);
          updatePairProgress(pairIndex, { phase: "error" });
        } finally {
          completedPairs += 1;
          refreshSubmitStatus();
        }
      }, PAIR_SUBMIT_CONCURRENCY);

      if (successCount > 0) {
        toast.success(
          `${successCount} push${successCount !== 1 ? "es" : ""} submitted for approval.`
        );
      }
      if (failCount > 0) {
        setSubmitError(errors.join(" | "));
        toast.error(`${failCount} push${failCount !== 1 ? "es" : ""} failed to submit.`);
      }
      // Only jump to Approvals on a clean submit — if anything failed, stay
      // put so submitError (only rendered in this wizard) is still visible
      // for the user to see which pair(s) need fixing/retrying.
      if (failCount === 0) {
        resetWizard();
        if (successCount > 0) onPushed?.();
      }
    } finally {
      setSubmitting(false);
      setSubmitStatus(null);
      setPairProgress({});
    }
  };

  const stepContent = [
    <Step1GroupAndFeedSelection
      selectedGroups={selectedGroups}
      onGroupToggle={handleGroupToggle}
      selectedFeedIds={selectedFeedIds}
      onFeedsChange={setSelectedFeedIds}
      onFeedsInfoChange={setGroupFeeds}
    />,
    <Step3VideoSelection
      selectedVideos={selectedVideos}
      onVideosChange={setSelectedVideos}
      groupFeeds={groupFeeds}
      selectedFeedIds={selectedFeedIds}
    />,
    <StepVideoSource
      selectedVideos={selectedVideos}
      groupFeeds={groupFeeds}
      selectedFeedIds={selectedFeedIds}
      videoSourceConfig={videoSourceConfig}
      onVideoSourceChange={handleVideoSourceChange}
      onRemoveVideo={handleRemoveVideo}
    />,
    <Step4ReviewSubmit
      selectedGroups={selectedGroups}
      selectedFeedIds={selectedFeedIds}
      groupFeeds={groupFeeds}
      selectedVideos={selectedVideos}
      overrides={overrides}
      onOverrideChange={handleOverrideChange}
      forcePush={forcePush}
      onForcePushChange={setForcePush}
    />,
  ];

  const NavButtons = () => (
    <Box display="flex" justifyContent="space-between" mt={2}>
      <Button
        variant="outlined"
        color="inherit"
        onClick={handleBack}
        disabled={activeStep === 0 || submitting}
        startIcon={<ArrowBackIcon />}
        sx={{ borderRadius: 2, textTransform: "none" }}
      >
        Back
      </Button>

      {isLastStep ? (
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting}
          endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          {submitting ? submitStatus || "Submitting…" : "Submit for Approval"}
        </Button>
      ) : (
        <Button
          variant="outlined"
          color="success"
          onClick={handleNext}
          disabled={!canGoNext}
          endIcon={<ArrowForwardIcon />}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Next
        </Button>
      )}
    </Box>
  );

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <NavButtons />
      {activeStep > 0 && selectedGroups.length > 0 && (
        <Paper variant="outlined" data-testid="sending-to" sx={{ mt: 2, p: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            Sending to
          </Typography>
          {selectedGroups.map((g) => {
            const feedCount = groupFeeds.filter(
              (f) => f._group_type === g.group_type && f._group_id === g.group_id && selectedFeedIds.includes(f.id)
            ).length;
            return (
              <Tooltip key={`${g.group_type}-${g.group_id}`} title={`Remove ${g.label} — its feeds and their videos will not be sent`}>
                <Chip
                  label={`${g.label} · ${feedCount} feed${feedCount !== 1 ? "s" : ""}`}
                  size="small"
                  color={g.group_type === "PARTNER" ? "warning" : "primary"}
                  variant="outlined"
                  disabled={submitting}
                  onDelete={() => handleRemoveGroup(g)}
                  data-testid={`remove-group-${g.group_type}-${g.group_id}`}
                />
              </Tooltip>
            );
          })}
        </Paper>
      )}
      <Box mt={2} mb={2}>
        {stepContent[activeStep]}
      </Box>
      {submitting && Object.keys(pairProgress).length > 0 && (
        <Box mt={1} mb={2}>
          {submitStatus && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {submitStatus}
            </Typography>
          )}
          {Object.entries(pairProgress).map(([idx, p]) => (
            <Box key={idx} mb={1}>
              <Box display="flex" justifyContent="space-between" gap={1}>
                <Typography variant="caption" noWrap sx={{ maxWidth: "75%" }}>
                  {p.label}
                </Typography>
                <Typography
                  variant="caption"
                  color={p.phase === "error" ? "error" : "text.secondary"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {p.phase === "error"
                    ? "Failed"
                    : p.phase === "done"
                    ? "Done"
                    : p.phase === "submitting"
                    ? "Submitting…"
                    : p.phase === "pending"
                    ? "Pending…"
                    : `${Math.round(p.percent)}%`}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={p.phase === "pending" ? 0 : p.percent}
                color={p.phase === "error" ? "error" : p.phase === "done" ? "success" : "primary"}
                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
              />
            </Box>
          ))}
        </Box>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}
      <NavButtons />
    </Box>
  );
}

const TAB_VALUES = ["new", "approvals"];

export default function PushToAdminPortal() {
  // Kept in the URL (?tab=new|approvals) rather than plain useState so a
  // page refresh (or a link straight to the Approvals view) lands back on
  // the same tab instead of always resetting to "New Push".
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab = TAB_VALUES.includes(rawTab) ? rawTab : "new";

  const setTab = (next) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", next);
        return params;
      },
      { replace: true }
    );
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Push to Admin Portal
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Push a WooGlobe (WGID-backed) video, or an approved CreatorVault-native video, straight
        into WooGlobe Admin Portal's own MRSS queue. Every push needs CreatorVault admin approval
        before anything reaches Admin Portal.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="New Push" value="new" />
        <Tab label="Approvals" value="approvals" />
      </Tabs>

      {tab === "new" ? (
        <NewPushWizard onPushed={() => setTab("approvals")} />
      ) : (
        <ApprovalsPanel />
      )}
    </Box>
  );
}
